// auth.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { RoleService } from 'src/app/core/services/APIs/user/role.service';
import { loadRoles, loadRolesSuccess } from '../actions/role.actions';
import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { CustomToastService } from 'src/app/shared/services/custom-toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Role } from 'src/app/core/models/role.model';

@Injectable()
export class RoleEffects {

    constructor(
        private actions$: Actions,
        private auth: AuthService,
        private store: Store,
        private roleService: RoleService,
        private customToast: CustomToastService,
        private translateService: TranslateService
    ) { }

    loadRoles$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadRoles),
                tap(() => {
                    // Check localStorage first
                    const storedRoles = localStorage.getItem('roles');
                    if (storedRoles) {
                        try {
                            const roles = JSON.parse(storedRoles).roles;
                            if (roles && Array.isArray(roles) && roles.length > 0) {
                                this.store.dispatch(loadRolesSuccess({ roles }));
                                return;
                            }
                        } catch (error: any) {
                            console.error('Error parsing stored roles:', error);
                            // Clear invalid roles data
                            localStorage.removeItem('roles');
                        }
                    }

                    // If no stored roles, get them from Auth0 user
                    this.auth.user$.subscribe((user: any) => {
                        if (!user) {
                            console.warn('No authenticated user found');
                            this.store.dispatch(loadRolesSuccess({ roles: [] }));
                            return;
                        }

                        // Use Auth0 user roles directly
                        if (user.roleType && Array.isArray(user.roleType) && user.roleType.length > 0) {
                            console.log('Using roles from Auth0 user information:', user.roleType);
                            const roles = this.formatRolesFromAuth0(user.roleType);
                            this.store.dispatch(loadRolesSuccess({ roles }));
                        } else {
                            console.warn('No roles available from Auth0 user');
                            this.store.dispatch(loadRolesSuccess({ roles: [] }));
                        }
                    });
                })
            ),
        { dispatch: false }
    );

    persistRole$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(loadRolesSuccess),
            tap(({roles}) => {
              localStorage.setItem('roles', JSON.stringify({ roles })); 
            })
          ),
        { dispatch: false }
      );

    /**
     * Format roles from Auth0 user information
     */
    private formatRolesFromAuth0(roleTypes: string[]): Role[] {
        return roleTypes.map(roleType => {
            // Trim the role name to remove any leading/trailing whitespace
            const trimmedRoleName = roleType.trim();
            
            return {
                id: trimmedRoleName.toLowerCase().replace(/\s+/g, '_'),
                name: trimmedRoleName,
                description: `Auth0 role: ${trimmedRoleName}`
            };
        });
    }
}
